import { createSelector } from 'reselect';
import { getSizotsaFuneralCover } from './index';
import { extendedFamilyRelations, getQuoteRefernceData, getTermsAndConditions, parentsInLawsRelations } from './referenceData';
import { SF_COVER_CATEGORIES, RELATIONS, DEFAULT_COVER_AMOUNTS } from '../../actions/sizotsaFuneralCover/types';
import { quotaionMockData, paidUpMock, incomeStabilizationMock } from '../../mocks/sizotsaFuneralCover/quote';
import { FORM_STATUS } from '../../reducers/status';
import { getPersonalDetailsSummary, getPurchasePersonalDetailsForm } from './personalDetails';
import { getBankingDetailsForm, getBankingDetailsSummary, getPaymentFrequency } from './bankingDetails';
import { getUploadDocumentForm, getUploadDocumentsSummary } from './uploadDocuments';

export const getQuote = createSelector(getSizotsaFuneralCover, (sizotsaFuneralCover) => sizotsaFuneralCover.quote);

export const getSelectedCoverCategory = createSelector(getQuote, (quote) => quote?.selectedCoverCategory);
export const getCoverType = createSelector(getQuote, (quote) => quote.coverType);
export const getDeleteQuoteDetails = createSelector(getQuote, (quote) => quote.deleteQuoteDetails);
export const getEditQuoteDetails = createSelector(getQuote, (quote) => quote.editQuoteDetails);
export const getQuoteForm = createSelector(getQuote, (quote) => quote.form);
export const getPopupModalValue = createSelector(getQuote, (quote) => quote?.popupModalDetails);
export const getLivesCovered = createSelector(getQuote, (quote) => quote.livesCovered);
export const getBeneficiaryForm = createSelector(getLivesCovered, (livesCovered) =>
    Object.values(livesCovered[SF_COVER_CATEGORIES.BENEFECIARY])
);
export const getAgentCodeValue = createSelector(getLivesCovered, (livesCovered) =>{
    const {agentCode} =  Object.values(livesCovered[SF_COVER_CATEGORIES.AGENT])[0]?? {agentCode:null}
    return agentCode
}
);
export const getBenefitsDetails = createSelector(
    getLivesCovered,
    (livesCovered) => Object.values(livesCovered[SF_COVER_CATEGORIES.BENIFITS])[0] ?? {}
);
export const getIncomeStabilization = createSelector(
    getLivesCovered,
    (livesCovered) => 
        {
            // console.log(Object.values(livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY])[0], 'getIncome');
            return Object.values(livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY])[0] ?? {} 
        }
);
export const getQuoteResponse = createSelector(getQuote, (quote) => quote.quotingResponse);

export const getIsOnInitPage = createSelector(getQuote, (quote) => {
    return !Object.values(quote.livesCovered).some((element) => Object.values(element).length);
});
export const getMainMemberDetails = createSelector(
    getLivesCovered,
    (livesCovered) => Object.values(livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY])[0]
);
export const getProductCodeSelected = createSelector(getLivesCovered, (livesCovered) =>
    Object.values(livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY]).length > 1
        ? SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY
        : SF_COVER_CATEGORIES.MYSELF
);
//Parents In Lows Dropdown disabled logic
export const getDisplayParentCoverOptions = createSelector(
    getLivesCovered,
    getQuoteRefernceData,
    (livesCovered, parentOptions) => {
        return parentOptions?.displayParentsandInLaws?.map(({ code, description }) => ({
            code: code,
            description: description,
            disabled: Object.values(livesCovered[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS]).some(
                (ele) => ele.relationship.value === code
            ),
        }));
    }
);
//Cover Categories Disabled logic

const beneficiaryRelations = [
    'Mother',
    'Mother-in-law',
    'Father',
    'Father-in-law',
    'Father-in-law',
    'Brother',
    'Sister',
];
export const getLivesCoveredMembersList = createSelector(getLivesCovered, (livesCovered) => {
    const allMembers = {
        ...livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY],
        ...livesCovered[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS],
        ...livesCovered[SF_COVER_CATEGORIES.EXTENDED_FAMILY],
    };
    return Object.keys(allMembers)?.flatMap((member) =>
        allMembers[member].relationship.value === 'Myself' || allMembers[member].age.value < 18
            ? []
            : { id: member, name: allMembers[member]?.firstName.value + ' ' + allMembers[member]?.surname.value }
    );
});

export const getMonthlyPremiumQuote = createSelector(getQuoteForm, (quoteForm) => {
    const { age, coverAmount, selectedCoverCategory, needIncomeStabilization, incomeStabilization } = quoteForm;
        const modifiedAge=age?.value
    const premiumCategory =
        needIncomeStabilization.value === 'Yes'
            ? incomeStabilizationMock[selectedCoverCategory.value][incomeStabilization.value]
            : quotaionMockData[selectedCoverCategory.value];

    if (premiumCategory && coverAmount.value) {
        return (
            premiumCategory[coverAmount.value]?.filter(
                ({ minAge, maxAge }) => modifiedAge >= minAge && modifiedAge <= maxAge
            )[0]?.premium ?? 0
        );
    } else return 0;
});

export const getMainCoverAmount = createSelector(
    getLivesCovered,
    (livesCovered) =>
        Object.values(livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY])?.filter(
            (item) => item.relationship.value === RELATIONS.MYSELF
        )[0]?.coverAmount.value ?? 0
);

export const getIncomeStabilizationPremium = createSelector(
    getQuoteForm,
    getMainCoverAmount,
    getProductCodeSelected,
    (quoteForm, mainMemberCoverAmount, productCode) => {
        const { age, coverAmount, incomeStabilization, selectedCoverCategory } = quoteForm;

        const premiumCategory = incomeStabilizationMock[productCode][incomeStabilization.value];
        if (premiumCategory && mainMemberCoverAmount) {
            return (
                premiumCategory[mainMemberCoverAmount]?.filter(
                    ({ minAge, maxAge }) => age.value >= minAge && age.value <= maxAge
                )[0]?.premium ?? 0
            );
        } else return 0;
    }
);
export const getMainMemberPremium = createSelector(
    getMainMemberDetails,
    getProductCodeSelected,
    (mainMemberDetails, productCode) => {
        const { age, coverAmount, needIncomeStabilization, incomeStabilization } = mainMemberDetails;

        const premiumCategory =
            needIncomeStabilization.value === 'Yes'
                ? incomeStabilizationMock[productCode][incomeStabilization.value]
                : quotaionMockData[productCode];

        if (premiumCategory && coverAmount.value) {
            return (
                premiumCategory[coverAmount.value]?.filter(
                    ({ minAge, maxAge }) => age.value >= minAge && age.value <= maxAge
                )[0]?.premium ?? 0
            );
        } else return 0;
    }
);

export const calculatePremium = (category, age, coverAmount, incomeStabilization) =>
    (incomeStabilization ? incomeStabilizationMock[category][incomeStabilization] : quotaionMockData[category])[
        coverAmount
    ]?.filter(({ minAge, maxAge }) => age >= minAge && age <= maxAge)[0]?.premium ?? 0;

export const getFinalQuotation = createSelector(
    getLivesCovered,
    getMainMemberDetails,
    (livesCovered, mainMemberDetails) => {
        const benifits = Object.values(livesCovered[SF_COVER_CATEGORIES.BENIFITS])[0];
        const cashbackDetails = benifits?.benefitsDetails?.value === 'Yes';
        const paidUpDetails = benifits?.benefitsDetailsOnPremium?.value === 'Yes';
        const { age, monthlyPremium } = mainMemberDetails;
        const percentage =
            paidUpMock.filter(({ minAge, maxAge }) => age.value >= minAge && age.value <= maxAge)[0]?.charge ?? 0;
        const costWithoutBenefits = Object.values(livesCovered)
            ?.flatMap((item) => Object.values(item))
            ?.reduce((acc, curr) => acc + curr.monthlyPremium.value, 0);

        const paidup = paidUpDetails
            ? costWithoutBenefits + (monthlyPremium?.value / 100) * percentage
            : costWithoutBenefits;
        const cashback = cashbackDetails ? paidup + (paidup / 100) * 8.6 : paidup;
        return cashback.toFixed(2);
    }
);

//Cover Categories Disabled logic
export const getLivesCoveredCategoryOptions = (withChild) =>
    createSelector(getLivesCovered, getQuoteRefernceData, (livesCovered) => {
        const membersCount = {
            [RELATIONS.SPOUSE]:
                Object.values(livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY])?.filter(
                    ({ relationship }) => relationship.value === RELATIONS.SPOUSE
                ).length === 2,
            [RELATIONS.CHILD]:
                Object.values(livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY])?.filter(
                    ({ relationship }) => relationship.value === RELATIONS.CHILD
                ).length === 6,
            [SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS]:
                Object.values(livesCovered[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS])?.length === 4,
            [SF_COVER_CATEGORIES.EXTENDED_FAMILY]:
                Object.values(livesCovered[SF_COVER_CATEGORIES.EXTENDED_FAMILY])?.length === 8,
        };
        return [
            ...(withChild
                ? [
                      {
                          code: 'Child',
                          description: 'Child',
                          disabled: membersCount.Child,
                      },
                      {
                          code: 'Spouse',
                          description: 'Spouse',
                          disabled: membersCount.Spouse,
                      },
                  ]
                : []),
            {
                code: SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS,
                description: 'My Parents and Parents In-law',
                disabled: membersCount[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS],
            },
            {
                code: SF_COVER_CATEGORIES.EXTENDED_FAMILY,
                description: 'My Extended Family',
                disabled: membersCount[SF_COVER_CATEGORIES.EXTENDED_FAMILY],
            },
        ];
    });

export const getLivesCoveredFormStatus = createSelector(getLivesCovered, (livesCovered) => {
    const membersCovered = [
        livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY],
        livesCovered[SF_COVER_CATEGORIES.EXTENDED_FAMILY],
        livesCovered[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS],
    ];
    return membersCovered
        .flatMap((member) => Object.values(member))
        .every((item) => item?.formStatus?.value === FORM_STATUS.VALID);
});
export const getDirectFamilyMemberDetailsAdded = createSelector(getLivesCovered, (livesCovered) =>
    Object.values(livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY])
        .filter((member) => member.relationship !== RELATIONS.MYSELF)
        .some((member) => member.firstName.value)
);
export const getHighestCoverAmount = createSelector(getLivesCovered, (livesCovered) => {
    const allMembersCoverAmount = Object.values({
        ...livesCovered[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS],
        ...livesCovered[SF_COVER_CATEGORIES.EXTENDED_FAMILY],
    }).map((member) => member.coverAmount.value);

    return Math.max(...allMembersCoverAmount);
});

const inputFieldLabels = {
    id: 'ID Number',
    dateOfBirth: 'Date Of Birth:',
    relationship: 'Relationship',
    cellphone: 'Cell Phone Number',
    email: 'Email Address',
};
export const getIsOldExtendedMemberAdded = createSelector(getLivesCovered, getQuoteForm, (livesCovered, quoteForm) => {
    const allMembers = {
        ...livesCovered[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS],
        ...livesCovered[SF_COVER_CATEGORIES.EXTENDED_FAMILY],
    };
    const maxlimit = DEFAULT_COVER_AMOUNTS[quoteForm.coverAmount?.value] ?? 15000;
    return Object.values(allMembers)?.some((member) => member?.age.value > 59 && member?.coverAmount.value > maxlimit);
});

export const getBenificiaryDetailsSummary = createSelector(getBeneficiaryForm, (beneFiciaryForm) => {
    // console.log(beneFiciaryForm[0]['firstName']?.value, beneFiciaryForm, 'summary');
    const fullName = beneFiciaryForm[0]['firstName']?.value + ' ' + beneFiciaryForm[0]['surname']?.value;
    const beneficiaryDetails = Object.keys(beneFiciaryForm[0])
        ?.flatMap((item) => {
            if (!Object.keys(inputFieldLabels).includes(item)) return;
            return {
                label: inputFieldLabels[item],
                value: beneFiciaryForm[0][item]?.value ?? '-',
            };
        })
        .filter((item) => item !== undefined);
    return [
        {
            label: 'Beneficiary Name',
            value: fullName,
        },
        ...beneficiaryDetails,
    ];
});

export const getLivesCoveredSummary = createSelector(getLivesCovered, (livesCovered) => {
    const allMembers = {
        ...livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY],
        ...livesCovered[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS],
        ...livesCovered[SF_COVER_CATEGORIES.EXTENDED_FAMILY],
    };
    return Object.keys(allMembers)?.flatMap((member) => ({
        id: member,
        fullName:
            allMembers[member]?.title?.value +
            ' ' +
            allMembers[member]?.firstName?.value +
            ' ' +
            allMembers[member]?.surname?.value,
        relationship: allMembers[member]?.relationship?.value,
        coverAmount: allMembers[member]?.coverAmount?.value ?? '-',
        monthlyPremium: allMembers[member]?.monthlyPremium?.value ?? '-',
    }));
});

export const getPlanDetailsSummary = createSelector(
    getQuoteResponse,
    getLivesCovered,
    getPaymentFrequency,
    (quoteSummary, livesCovered, paymentFrequency) => {
        return [
            {
                label: 'Plan Name',
                value: `Sizotsa Funeral Cover ${
                    Object.keys(livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY]).length > 1
                        ? '(Me & My Direct Family)'
                        : '(Myself)'
                }`,
            },
            {
                label: 'Premium Frequency',
                value: paymentFrequency === 'Monthly' ? 'Monthly' : 'Yearly',
            },
            {
                label: 'Premium Contribution',
                value: `E ${
                    paymentFrequency === 'Monthly'
                        ? quoteSummary?.funeralPremium ?? 0
                        : getValueWithThousandSeperator((quoteSummary?.funeralPremium*12 ?? 0)?.toFixed(2))
                }`,
            },
        ];
    }
);
export const getMaxCountOfAllmembers = createSelector(getLivesCovered, (livesCovered) => {
    const allMembers = {
        ...livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY],
        ...livesCovered[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS],
        ...livesCovered[SF_COVER_CATEGORIES.EXTENDED_FAMILY],
    };
    return Object.keys(allMembers).filter((member) => allMembers[member]?.age?.value > 18).length;
});
export const getMaxMembersCountReached = createSelector(getLivesCovered, (livesCovered) => {
    const extraAddedMembers = {
        ...livesCovered[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS],
        ...livesCovered[SF_COVER_CATEGORIES.EXTENDED_FAMILY],
    };
    return Object.values(extraAddedMembers).length === 12;
});
export const getAllMembersCountReached = createSelector(getLivesCovered, (livesCovered) => {
    const extraAddedMembers = {
        ...livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY],
        ...livesCovered[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS],
        ...livesCovered[SF_COVER_CATEGORIES.EXTENDED_FAMILY],
    };
    return Object.values(extraAddedMembers).length === 21;
});

export const getBooleanValue = (value) => value?.toLowerCase() === 'yes';

export const getPurchaseDetailsRequestBody = createSelector(
    getPurchasePersonalDetailsForm,
    getLivesCovered,
    getBeneficiaryForm,
    getBankingDetailsForm,
    getUploadDocumentForm,
    getTermsAndConditions,
    getAgentCodeValue,
    getIncomeStabilization,
    (personalDetails, livesCovered, beneFiciaryForm, bankingDetails, documentDetails, tandCDetails,agentCode,{needIncomeStabilization,incomeStabilization}) => {
        const personalDetailsPayload = {
            title: personalDetails?.title?.value,
            firstName: personalDetails?.firstName?.value,
            lastName: personalDetails?.surname?.value,
            nationality: personalDetails?.nationality?.value,
            birthCountry: personalDetails?.birthCountry?.value,
            agentCode:agentCode?.value,
            botswanaIdNumber: personalDetails?.id?.value,
            dateOfBirth: personalDetails?.dateOfBirth?.value?.replaceAll('/', '-'),
            idExpiryDate: personalDetails?.expiryDate?.value?.replaceAll('/', '-'),
            // idExpiryDate: personalDetails?.expiryDate?.value?.replaceAll('/', '-'),
            isPip: getBooleanValue(personalDetails?.prominentPerson?.value),
            email: personalDetails?.email?.value,
            contactNumber: personalDetails?.mobileNumber?.value,
            residentialAddress: {
                addressLine1: personalDetails?.streetAddress?.value,
                city: personalDetails?.city?.value,
                country: personalDetails?.country?.value,
            },
            postalAddress: {
                addressLine1: personalDetails?.postalSuburb?.value,
                city: personalDetails?.postalCity?.value,
                country: personalDetails?.postalCountry?.value,
            },
            previousResidentialAddress: {
                addressLine1: personalDetails?.previousPostalAddress?.value ?? '',
                city: personalDetails?.previousPostalCity?.value ?? '',
                country: personalDetails?.previousPostalCountry?.value ?? '',
            },
            employmentDetails: {
                status: personalDetails?.employementType?.value,
                occupation: personalDetails?.employeeOccupation?.value,
                sourceOfIncome: personalDetails?.sourceOfIncome?.value,
            },
        };

        const benifitsPayload = {
            premiumPaidUp: false,
            cashBack: getBooleanValue(livesCovered[SF_COVER_CATEGORIES.BENIFITS][0]?.benefitsDetails?.value),
            incomeStabilization: needIncomeStabilization?.value === "Yes",
            incomeStabilizationAmount: needIncomeStabilization?.value === "Yes" ? +incomeStabilization.value : 0
        };
        const allMembers = {
            ...livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY],
            ...livesCovered[SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS],
            ...livesCovered[SF_COVER_CATEGORIES.EXTENDED_FAMILY],
        };
        const livesCoveredPayload = Object.keys(allMembers)?.flatMap((member) => ({
            relationship: extendedFamilyRelations.some(
                (item) => item?.description === allMembers[member]?.relationship?.value
            )
                ? 'My_Extended_Family'
                : parentsInLawsRelations.some((item) => item?.description === allMembers[member]?.relationship?.value)
                ? 'MyParents_and_In-laws'
                : allMembers[member]?.relationship?.value,
            title: allMembers[member]?.title?.value,
            firstName: allMembers[member]?.firstName?.value,
            lastName: allMembers[member]?.surname?.value,
            dateOfBirth: allMembers[member]?.dateOfBirth?.value?.replaceAll('/', '-'),
            nationality: allMembers[member]?.nationality?.value,
            botswanaId: allMembers[member]?.id?.value,
            coverAmount: allMembers[member]?.coverAmount?.value,
            isStudent: getBooleanValue(allMembers[member]?.childIsStudent?.value),
            hasSpecialNeeds: getBooleanValue(allMembers[member]?.childHaveSpecialNeed?.value),
            familyMember: allMembers[member]?.relationship?.value
        }));

        const beneficiariesPayload = {
            name: beneFiciaryForm[0]?.firstName?.value,
            lastName: beneFiciaryForm[0]?.surname?.value,
            nationalId: beneFiciaryForm[0]?.id?.value,
            dateOfBirth: beneFiciaryForm[0]?.dateOfBirth?.value.replaceAll('/', '-'),
            relationship: beneFiciaryForm[0]?.relationship?.value,
            // addressLine1: beneFiciaryForm?.fileName?.value,
            // addressLine2: beneFiciaryForm?.fileName?.value,
            // city: beneFiciaryForm?.fileName?.value,
            email: beneFiciaryForm[0]?.email?.value,
            mobileNumber: beneFiciaryForm[0]?.cellphone?.value,
            // percentageBenefit: 100,
            nationality: beneFiciaryForm[0]?.nationality?.value,
        };

        const paymentPayload = {
            bankName: bankingDetails?.bankName?.displayValue,
            branchName: bankingDetails?.branchCode?.displayValue,
            accountHolderName: bankingDetails?.accountHolder?.value,
            accountNumber: bankingDetails?.accountNumber?.value?.replaceAll(' ', ''),
            accountType: bankingDetails?.accountType?.displayValue,
            debitOrderDate: bankingDetails?.debitOrderDay?.value,
            confirmDetails: bankingDetails?.paymentAuthorization?.value,
        };
        const inputFieldLabels = {
            certifiedCopyOfId: 'Your ID',
            incomeProof: 'Proof of income statement',
            addressProof: 'Proof of address',
            bankStatement: 'Bank statement',
        };
        const documentsPayload = Object.keys(documentDetails)
            .map((key) => {
                 const currentUrlSplited=documentDetails[key]?.url?documentDetails[key]?.url.split("file/"):["",""]
                return {
                    documentType: inputFieldLabels[key],
                    fileName: documentDetails[key]?.fileName,
                    fileType: 'image/png',
                    fileSize: 2048000,
                    url: currentUrlSplited[0]+'file/'+encodeURIComponent(currentUrlSplited[1]),
                };
            })
            .filter((item) => item?.fileName);
        return {
            coverType:
                Object.keys(livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY]).length > 1
                    ? 'Me_And_My_Direct_Family'
                    : SF_COVER_CATEGORIES.MYSELF,
            personalDetails: personalDetailsPayload,
            livesCovered: livesCoveredPayload,
            beneficiaries: [beneficiariesPayload],
            paymentDetails: paymentPayload,
            uploadDocuments: documentsPayload,
            benifit: benifitsPayload,
            confirmDetails: tandCDetails,
        };
    }
);

export const getBenefitDetailsSummary = createSelector(getBenefitsDetails, (benefitDetails) => {
    const benefitsLabel = {
        benefitsDetails: 'Cash Back',
    };
    return Object.keys(benefitDetails)
        ?.flatMap((item) => {
            if (!Object.keys(benefitsLabel).includes(item)) return;
            return {
                label: benefitsLabel[item],
                value: benefitDetails[item]?.value ?? '-',
            };
        })
        .filter((item) => item !== undefined);
});

export const getValueWithThousandSeperator = (amount) => {
    let decimalSeperate = amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimalSeperate ?? 0;
};

const categoryKeys = {
    [SF_COVER_CATEGORIES.EXTENDED_FAMILY]: 'MyExtendedFamily',
    [SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS]: 'MyParentsOrInLaws',
};

const getValidDOB = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 17);

    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0"); 
    const year = today.getFullYear();

    return `${day}/${month}/${year}`;
};

export const getQuoteRequestBody = createSelector(
    getMainMemberDetails,
    getLivesCovered,
    getBenefitsDetails,
    getIncomeStabilization,
    (mainMember, livesCovered, benifitsDetails,{incomeStabilization,needIncomeStabilization}) => {
        const { dateOfBirth, addPartner, addChild, coverAmount, gender, title } = mainMember;
        const { benefitsDetails} = benifitsDetails;
        

        const getAdditionalMembers = (category) =>
            Object.keys(livesCovered[category])?.map((memberKey) => {
                const { dateOfBirth, coverAmount,relationship } = livesCovered[category][memberKey];
                // console.log(relationship,'relationship');
                return {
                    id: memberKey,
                    familyMemberType: categoryKeys[category],
                    familyMemberRelation: relationship?.value,
                    dateOfBirth: dateOfBirth.value,
                    coverAmount: +coverAmount.value,
                };
            });
        const allMembersParentinLaws = getAdditionalMembers(SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS);
        const allMembersExtendedFamily = getAdditionalMembers(SF_COVER_CATEGORIES.EXTENDED_FAMILY);
        const dynamicDOB = getValidDOB(); 
        // console.log(incomeStabilization, 'income Stabilization')
        // Child Special needs and Student changes 
        const childDetails = Object.values(livesCovered[SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY]).map((item)=> {
            const child = { 
                ...item,
                childIsStudent: {
                    ...item.childIsStudent, 
                    value: item.childIsStudent.value === 'Yes' ? 'CHECKED' : 'UNCHECKED'
                },
                childHaveSpecialNeed: {
                    ...item.childHaveSpecialNeed, 
                    value: item.childHaveSpecialNeed.value === 'Yes' ? 'CHECKED' : 'UNCHECKED'
                }
            }
            return child
        }).filter(({relationship}) => relationship?.value === 'Child')


        let childDetailPayload = []

        for (let key in childDetails) {
            let childData = childDetails[key]
            childDetailPayload.push({ 
                    dateOfBirth: childData.dateOfBirth.value ?? dynamicDOB,
                    specialNeeds: childData.childHaveSpecialNeed.value ?? 'UNCHECKED',
                    student: childData.childIsStudent.value  ?? 'UNCHECKED',
            })
        }
        return {
            dateOfBirth: dateOfBirth?.value,
            gender: gender?.value.toLowerCase(),
            coverAmount: +coverAmount?.value,
            spouseCovered: addPartner?.value,
            spouseDateOfBirth: dateOfBirth?.value,
            childrenCovered: addChild?.value,
            // childrenDetails: [
            //     {
            //         dateOfBirth: dynamicDOB,
            //     },
            // ],
            childrenDetails: childDetailPayload,
            paymentFrequency: 'monthly',
            benefitsApplied: {
                cashback: getBooleanValue(benefitsDetails?.value),
                premiumPaidUp: false,
                incomeStabilizationAmount: needIncomeStabilization?.value ==="Yes" ? +incomeStabilization.value : 0,
            },
            additionalCoverMembers: [...allMembersExtendedFamily, ...allMembersParentinLaws],
        };
    }
);

export const getLeadMailPayload = createSelector(getLivesCovered, (livesCovered) => {
    const getAdditionalMembers = (category) => {
        const members = livesCovered[category]; // This retrieves the members object
        return Object.entries(members).flatMap(([memberKey, member]) => {
            const result = [];
            const dobValue = member?.dateOfBirth.value;
            if (dobValue) {
                result.push({
                    label: 'Date of Birth',
                    value: dobValue,
                });
            }
            const coverAmountValue = member?.coverAmount.value;
            if (coverAmountValue !== null) {
                result.push({
                    label: 'Cover Amount',
                    value: coverAmountValue,
                });
            }
            if (member.needIncomeStabilization.value === 'Yes') {
                result.push({
                    label: 'Income Stabilization',
                    value: member.incomeStabilization.value,
                });
            }

            return result;
        });
    };
    const meAndmyFamily = getAdditionalMembers(SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY);
    const parentsinLaws = getAdditionalMembers(SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS);
    const extendedFamily = getAdditionalMembers(SF_COVER_CATEGORIES.EXTENDED_FAMILY);
    const benefits = Object.values(livesCovered[SF_COVER_CATEGORIES.BENIFITS]);

    return [
        {
            title: meAndmyFamily?.length > 1 ? SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY : SF_COVER_CATEGORIES.MYSELF,
            data: [...meAndmyFamily],
        },
        parentsinLaws?.length && { title: SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS, data: [...parentsinLaws] },
        extendedFamily?.length && { title: SF_COVER_CATEGORIES.EXTENDED_FAMILY, data: [...extendedFamily] },
        benefits.length && {
            title: 'Benefits',
            data: [
                {
                    label: 'Cash back',
                    value: benefits[0].benefitsDetails?.value,
                },
            ],
        },
    ].filter((item) => item !== 0);
});

export const getLivesCoveredSummaryPayload = createSelector(getLivesCovered, (livesCovered) => {
    const getMemberSummary = (title, category) => {
        return Object.values(livesCovered[category])?.flatMap((member) => ({
            title: title,
            data: [
                {
                    label: 'Full name',
                    value: member?.title?.value + ' ' + member?.firstName?.value + ' ' + member?.surname?.value,
                },
                {
                    label: 'Relation',
                    value: member?.relationship?.value,
                },
                {
                    label: 'Cover amount',
                    value: member?.coverAmount?.value,
                },
                {
                    label: 'Premium',
                    value: member?.monthlyPremium?.value,
                },
            ],
        }));
    };
    return [
        ...getMemberSummary('Me and my Direct family', SF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY),
        ...getMemberSummary('Parents in laws', SF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS),
        ...getMemberSummary('Extended family', SF_COVER_CATEGORIES.EXTENDED_FAMILY),
        ...getMemberSummary('Beneficiary', SF_COVER_CATEGORIES.BENEFECIARY),
    ];
});

export const getPurchaseMailData = createSelector(
    getPersonalDetailsSummary,
    getLivesCoveredSummaryPayload,
    getBenefitDetailsSummary,
    getUploadDocumentsSummary,
    getBankingDetailsSummary,
    (personalDetailsData, livesCoveredSummary, benefitsSummary, uploadDocumentsSummary,bankingDetailsSummary) => {

        const { personalDetails, contactDetails, addressDetails } = personalDetailsData;
        const personalDetailsSummary = [
            { title: 'Personal Details', data: personalDetails },
            { title: 'Contact Details', data: contactDetails },
            { title: 'Address Details', data: addressDetails },
        ];
        const benefitDetails=[
            { title: 'Benefit Details', data: benefitsSummary },
        ]
        const bankingDetails=[
            { title: 'Banking Details', data: bankingDetailsSummary },
        ]
        const documentDetails=[
            { title: 'Document Details', data: uploadDocumentsSummary },
        ]

        return [...personalDetailsSummary, ...livesCoveredSummary, ...benefitDetails, ...bankingDetails, ...documentDetails];
    }
);
